import { IDirectionWorldMarker } from 'react-give-me-map';
import { CtxEdit } from 'session/ctxEdit';

import 'components/site/general/singleSelect/SelectedMarkerV2/style.css';
import { useContext, useEffect, useState } from 'react';

import { useDirectMarkerUpdates } from 'components/minute/builders/v2/useDirectMarkerUpdates';
import { DragPoints } from 'components/site/general/singleSelect/SelectedMarkerV2/DragPoints';
import { SelectArea } from 'components/site/general/singleSelect/SelectedMarkerV2/SelectedArea';
import 'components/site/general/singleSelect/SelectedMarkerV2/style.css';
import { useCoordinatesActions } from 'components/site/general/singleSelect/SelectedMarkerV2/useCoordinatesActions';
import { ICoordinates } from 'libs/types/map/mapTypes';
import { throttle } from 'lodash';
import type { MapboxGeoJSONFeature, MapMouseEvent } from 'mapbox-gl';
import polylabel from 'polylabel';
import { IXY } from 'react-give-me-map/dist/utils/map/mapTypes';
import { getTransportPath } from 'static/externalApi/directionApi';
import { useUpdateEffect } from 'usehooks-ts';

interface Props {
    markerId: string;
}

export const SelectedMarkerV2Direction = (props: Props) => {
    const ctxEdit = useContext(CtxEdit);
    const directMarkerUpdates = useDirectMarkerUpdates();

    const marker = ctxEdit.worldMarkers.find(
        (e) => e.id === props.markerId
    ) as IDirectionWorldMarker;

    const getPath = throttle(
        async (coordinates: ICoordinates[]) => {
            return await getTransportPath(marker.elementData.transport, coordinates);
        },
        500,
        { leading: true, trailing: true }
    );

    const updateMarkerDirectly = async (newCoordinates: ICoordinates[]) => {
        const { path, waypoints } = await getPath(newCoordinates);
        directMarkerUpdates.updateDirection(marker.id, waypoints, path);
    };

    const markerId = marker.id;
    const coordinates = marker.elementData.coordinates;
    const color = marker.elementData.color;

    const coordinateActions = useCoordinatesActions(ctxEdit.mapRef, coordinates);
    const [currentDrag, setCurrentDrag] = useState<'point' | 'map' | null>(null);

    function getInitialXyCoordinates(): IXY[] {
        return coordinates.map((c) => ctxEdit.mapRef.current?.project([c.lng, c.lat]));
    }

    const [xyCoordinates, setXyCoordinates] = useState(() => getInitialXyCoordinates());

    useEffect(() => {
        const onMoveOrZoom = () => {
            setXyCoordinates(getInitialXyCoordinates());
            setCurrentDrag('map');
        };
        const onMoveEndOrZoomEnd = () => {
            setCurrentDrag(null);
        };
        ctxEdit.mapRef.current?.on('move', onMoveOrZoom);
        ctxEdit.mapRef.current?.on('zoom', onMoveOrZoom);
        ctxEdit.mapRef.current?.on('moveend', onMoveEndOrZoomEnd);
        ctxEdit.mapRef.current?.on('zoomend', onMoveEndOrZoomEnd);
        return () => {
            ctxEdit.mapRef.current?.off('move', onMoveOrZoom);
            ctxEdit.mapRef.current?.off('zoom', onMoveOrZoom);
            ctxEdit.mapRef.current?.off('moveend', onMoveEndOrZoomEnd);
            ctxEdit.mapRef.current?.off('zoomend', onMoveEndOrZoomEnd);
        };
    }, [ctxEdit.mapRef.current, coordinates]);

    useUpdateEffect(() => {
        setXyCoordinates(getInitialXyCoordinates());
    }, [marker.elementData.coordinates]);

    const updateMarkerGlobally = async (newCoordinates: ICoordinates[]) => {
        const [lat, lng] = polylabel([newCoordinates.map(({ lat, lng }) => [lat, lng])]);

        const { path, waypoints } = await getTransportPath(
            marker.elementData.transport,
            newCoordinates
        );

        console.log('path', path);
        console.log('waypoints', waypoints);

        ctxEdit.updateWorldMarker(markerId, { lat, lng }, { coordinates: waypoints, path });
    };

    useEffect(() => {
        const onMouseClick = (e: MapMouseEvent & { features?: MapboxGeoJSONFeature[] }) => {
            const newPoint = { lat: e.lngLat.lat, lng: e.lngLat.lng };
            const index = e.features?.[0].properties?.segmentIndex;
            if (index != null) {
                const newCoordinates = [...coordinates];
                newCoordinates.splice(index + 1, 0, newPoint);
                updateMarkerGlobally(newCoordinates);
            }
        };

        const allClickableIds = new Array(marker.elementData.coordinates.length - 1)
            .fill(0)
            .map((_, i) => `${marker.id}|${i}|clickable`);

        allClickableIds.forEach((clickableId) =>
            ctxEdit.mapRef.current?.on('click', clickableId, onMouseClick)
        );

        return () => {
            allClickableIds.forEach((clickableId) =>
                ctxEdit.mapRef.current?.off('click', clickableId, onMouseClick)
            );
        };
    }, [ctxEdit.mapRef.current, marker.elementData.coordinates]);

    return (
        <SelectArea>
            <>
                {(currentDrag === null || currentDrag === 'point') && (
                    <DragPoints
                        xyCoordinates={xyCoordinates}
                        color={color}
                        onDrag={(xyCoordinate, index) => {
                            if (!currentDrag) {
                                setCurrentDrag('point');
                            }
                            updateMarkerDirectly(
                                coordinateActions.getForUpdate(xyCoordinate, index)
                            );
                        }}
                        onDragEnd={(xyCoordinate, index) => {
                            updateMarkerGlobally(
                                coordinateActions.getForUpdate(xyCoordinate, index)
                            );
                            setCurrentDrag(null);
                        }}
                    />
                )}
            </>
        </SelectArea>
    );
};
